/**
 * Sidebar Icon Map - Maps icon string names from API to Lucide React components
 *
 * The backend returns icon names as strings (e.g., "Home", "Users").
 * This utility maps those strings to actual Lucide icon components for rendering.
 * Extracted from EcgridPackageTestPage for reuse across sidebar consumers.
 */

import type { SidebarMenuItem } from '@/components/ui';
import {
  AlertTriangle,
  AtSign,
  BarChart,
  BarChart3,
  Bell,
  Building2,
  ClipboardList,
  Code,
  Contact,
  Copy,
  CreditCard,
  FileText,
  Gauge,
  GitBranch,
  Globe,
  HelpCircle,
  Home,
  LayoutDashboard,
  LifeBuoy,
  List,
  Mail,
  MailOpen,
  Mails,
  Network,
  Newspaper,
  PackageCheck,
  PlusSquare,
  Radio,
  SearchCheck,
  Settings,
  Shuffle,
  StickyNote,
  TrendingUp,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { SidebarMenuItemData } from '@/application/queries/sidebarMenu';

/**
 * Icon name → Lucide component mapping
 * Keys match the icon strings returned by GET /api/v1/sidebar/menu
 */
const iconMap: Record<string, LucideIcon> = {
  // General
  Home,
  Gauge,

  // Admin menu
  Building2,
  Wrench,
  PackageCheck,
  LayoutDashboard,
  TrendingUp,

  // Customer portal
  BarChart3,
  Mails,
  GitBranch,
  Copy,
  CreditCard,
  ClipboardList,
  Newspaper,

  // Support
  LifeBuoy,
  HelpCircle,

  // Management
  Globe,
  StickyNote,
  Users,
  Mail,
  MailOpen,

  // Special networks
  AtSign,

  // Admin sub-menus
  AlertTriangle,
  FileText,
  SearchCheck,
  Radio,
  Network,
  Settings,
  Bell,
  List,
  BarChart,
  Code,
  Contact,
  Shuffle,
  PlusSquare,
};

/**
 * Recursively maps SidebarMenuItemData (with string icon names)
 * to SidebarMenuItem (with Lucide icon components)
 */
export function mapMenuItemsWithIcons(items: SidebarMenuItemData[]): SidebarMenuItem[] {
  return items.map((item) => ({
    id: item.id,
    title: item.title,
    type: item.type,
    path: item.path,
    isActive: item.isActive,
    showSeparator: item.showSeparator,
    icon: typeof item.icon === 'string'
      ? (iconMap[item.icon] as SidebarMenuItem['icon'])
      : undefined,
    children: item.children ? mapMenuItemsWithIcons(item.children) : undefined,
  }));
}
